import React, { useState, useEffect, useMemo, useId } from "react";
import {
  FacetNavigation,
  SectionGroupItem,
  SectionStateAndComponent,
  SectionGroup,
} from "./Filters";
import PlusFilter from "./PlusFilter";
import QuickShipFilter from "./QuickShipFilter";
import VendorFilter from "./VendorFilter";
import SpecificationFilter from "./SpecificationFilter";
import PartsFilter from "./PartsFilter";
import CategoryFilter from "./CategoryFilter";
import SearchWithinFilter from "./SearchWithinFilter";
import FilterSort from "./FilterSort";
import { GroupProps } from "./GroupProps";
import isMobile from "~/common/helpers/isMobile";
import PartsFinder from "./PartsFinder";
import { CompatibleModel } from "~/common/Store/models/Category";
import OutletConditionFilter from "./OutletConditionFilter";

interface Props {
  filterContainer: React.RefObject<HTMLDivElement>;
  facetNavigation: FacetNavigation;
  pageUrl: string;
  showSearchWithinFilter?: boolean;
  showCategoryFilter?: boolean;
  showOutletConditionFilter?: boolean;
  filterComponentOrder?: ((props: GroupProps) => JSX.Element)[];
  showPartsFilter?: boolean;
  prefetch?: boolean;
  compatibleModels?: CompatibleModel[];
  isFilterRemoved?: boolean;
}

function setCollapsed(
  sectionGroups?: Readonly<SectionGroup[]>,
  collapse?: boolean,
): SectionGroup[] {
  return (sectionGroups || []).map((sectionGroup) => ({
    ...sectionGroup,
    collapsed: !!collapse,
  }));
}

const FilterList = ({
  facetNavigation,
  filterContainer,
  pageUrl,
  showSearchWithinFilter = true,
  showCategoryFilter = true,
  showOutletConditionFilter = false,
  filterComponentOrder,
  showPartsFilter = true,
  prefetch,
  compatibleModels,
  isFilterRemoved,
}: Props) => {
  const [runMobileOnce, setRunMobileOnce] = useState(true);
  const [userInteraction, setUserInteraction] = useState(false);

  const categoryFilter = facetNavigation.categoryFacets.sectionGroups;
  const [categoryGroup, setCategoryGroup] = useState(
    setCollapsed(categoryFilter),
  );

  const plusFilter = (
    facetNavigation.specificationFacets.sectionGroups || []
  ).find((group) => group.groupName === "WebstaurantPlus");
  const [plusGroup, setPlusGroup] = useState(
    plusFilter ? setCollapsed([plusFilter]) : [],
  );

  const outletConditionFilter = (
    facetNavigation.specificationFacets.sectionGroups || []
  ).find((group) => group.groupName === "Condition");
  const [outletConditionFilterGroup, setOutletConditionFilterGroup] = useState(
    outletConditionFilter ? setCollapsed([outletConditionFilter]) : [],
  );

  const quickShipFilter = (
    facetNavigation.specificationFacets.sectionGroups || []
  ).find((group) => group.groupName === "Quick Ship");
  const [quickShipFilterGroup, setQuickShipFilterGroup] = useState(
    quickShipFilter ? setCollapsed([quickShipFilter]) : [],
  );

  const brandFilter = facetNavigation.brandFacets.sectionGroups;
  const [vendorGroup, setVendorGroup] = useState(setCollapsed(brandFilter));

  const partsFilter = (facetNavigation.partFacets.sectionGroups || []).map(
    (group) => {
      return { ...group, groupName: "Parts" };
    },
  );
  const [partsGroup, setPartsGroup] = useState(setCollapsed(partsFilter));

  const specificationFilter = (
    facetNavigation.specificationFacets.sectionGroups || []
  )
    .filter((group) => group.groupName !== "WebstaurantPlus")
    .filter((group) => group.groupName !== "Condition")
    .filter((group) => group.groupName !== "Quick Ship");

  const [specificationGroup, setSpecificationGroup] = useState(
    setCollapsed(specificationFilter),
  );

  const searchWithinFilter = facetNavigation.searchWithinFacet.sectionGroups;
  const [searchWithinGroup, setSearchWithinGroup] = useState(
    setCollapsed(searchWithinFilter),
  );

  const categoryStateAndComponent = {
    Component: CategoryFilter,
    sectionGroups: categoryGroup,
    setState: setCategoryGroup,
    showComponent: true,
  };

  const specificationStateAndComponent = {
    Component: SpecificationFilter,
    sectionGroups: specificationGroup,
    setState: setSpecificationGroup,
    showComponent: true,
  };

  const sectionStateAndComponents: SectionStateAndComponent[] = [
    categoryStateAndComponent,
    {
      Component: PlusFilter,
      sectionGroups: plusGroup,
      setState: setPlusGroup,
      showComponent: true,
    },
    {
      Component: OutletConditionFilter,
      sectionGroups: outletConditionFilterGroup,
      setState: setOutletConditionFilterGroup,
      showComponent: showOutletConditionFilter,
    },
    {
      Component: QuickShipFilter,
      sectionGroups: quickShipFilterGroup,
      setState: setQuickShipFilterGroup,
      showComponent: true,
    },
    {
      Component: VendorFilter,
      sectionGroups: vendorGroup,
      setState: setVendorGroup,
      showComponent: true,
    },
    {
      Component: PartsFilter,
      sectionGroups: partsGroup,
      setState: setPartsGroup,
      showComponent: showPartsFilter,
    },
    specificationStateAndComponent,
    {
      Component: SearchWithinFilter,
      sectionGroups: searchWithinGroup,
      setState: setSearchWithinGroup,
      showComponent:
        showSearchWithinFilter &&
        (facetNavigation.productsCount + facetNavigation.partsCount > 1 ||
          !!facetNavigation.appliedFacetValues?.searchWithinFacets?.length),
      inputId: "search_within_" + useId(),
    },
  ];
  if (filterComponentOrder) {
    sectionStateAndComponents.sort(FilterSort(filterComponentOrder));
  }

  const groupsWithMultipleSelections = useMemo(
    () =>
      sectionStateAndComponents.reduce((withMultipleSelections, section) => {
        const inGroup = section.sectionGroups.reduce(
          (accInGroup, sectionGroup) => {
            let existingSelectedItemInGroup = false;

            const moreThanOneInGroup = sectionGroup.groupItems.some(
              (groupItem) => {
                if (groupItem.isSelected) {
                  if (existingSelectedItemInGroup) return true;

                  existingSelectedItemInGroup = groupItem.isSelected;
                }

                return false;
              },
            );

            return accInGroup + Number(moreThanOneInGroup);
          },
          0,
        );

        return withMultipleSelections + inGroup;
      }, 0),
    [sectionStateAndComponents],
  );

  const hasSelectedCategoryFilter = categoryGroup.some((group) =>
    group.groupItems.some((item) => item.isSelected),
  );

  const hasMoreThanOneCategoryGroup = categoryGroup
    ? categoryGroup.length > 1
    : false;

  const hasOneCategoryGroupWithMoreThanTwoItems = categoryGroup
    ? categoryGroup.length === 1 && categoryGroup[0]!.groupItems.length > 2
    : false;

  const anyFiltersApplied =
    sectionStateAndComponents.some((s) =>
      s.sectionGroups.some((group) =>
        group.groupItems.some((groupItem) => groupItem.isSelected),
      ),
    ) || pageUrl.includes("withinval=");

  categoryStateAndComponent.showComponent =
    showCategoryFilter &&
    (hasSelectedCategoryFilter ||
      ((hasMoreThanOneCategoryGroup ||
        hasOneCategoryGroupWithMoreThanTwoItems) &&
        !anyFiltersApplied));

  specificationStateAndComponent.showComponent =
    hasSelectedCategoryFilter || !categoryStateAndComponent.showComponent;

  useEffect(() => {
    if (isMobile("599px") && runMobileOnce) {
      setRunMobileOnce(false);
      // In mobile view set all the sections to collapsed.
      sectionStateAndComponents.forEach(({ sectionGroups, setState }) => {
        return setState(setCollapsed(sectionGroups, true));
      });
    }
  }, [runMobileOnce, sectionStateAndComponents]);

  /**
   * Go through each Section and Group in reverse and collapse until filters are shorter than
   * the product boxes height + toppers height - paging height (if toppers/paging exists).
   */
  useEffect(() => {
    if (userInteraction) return;

    const listing = document.querySelector("#product_listing");

    if (!listing || !filterContainer.current) return;

    // we need to include the topper height if toppers exists
    let topperHeight = 0;
    const toppers = document.querySelector("#toppers");

    if (toppers) {
      topperHeight = toppers.getBoundingClientRect().height;
    }

    // we need to exclude the paging height if paging exists
    let pagingHeight = 0;
    const paging = document.querySelector("#paging");

    if (paging) {
      pagingHeight = paging.getBoundingClientRect().height;
    }

    const listingHeight =
      listing.getBoundingClientRect().height + topperHeight - pagingHeight;

    const sidebarHeight =
      filterContainer.current.getBoundingClientRect().height;

    if (sidebarHeight <= listingHeight) return;

    /**
     * If the height of .filter-list__section changes, update these values.
     */
    const topAndBottomMargin = 16;
    const oneRowItemHeight = 29;
    const twoRowsItemHeight = 45;
    const showMoreButtonHeight = 32;
    const colorItemHeight = 45.06;
    const colorTopAndBottomMargin = 20;

    const getGroupItemsHeight = (items: readonly SectionGroupItem[]) => {
      if (!items) {
        return 0;
      }

      return items.reduce(
        (totalHeight, item) =>
          item.label.length > 22
            ? totalHeight + twoRowsItemHeight
            : totalHeight + oneRowItemHeight,
        0,
      );
    };

    let diff = sidebarHeight - listingHeight;

    /**
     * Calculate how much height each group would take and start collapsing
     *  until the sidebar's height is less than main's height.
     * Notes: We don't collapse category filters or Search Within
     */
    sectionStateAndComponents
      .slice()
      .filter(
        (x) =>
          x.Component !== CategoryFilter &&
          x.Component !== SearchWithinFilter &&
          x.showComponent,
      )
      .reverse()
      .some(({ sectionGroups, setState }) => {
        let modifiedSectionGroups = sectionGroups;
        const done = sectionGroups
          .slice()
          .reverse()
          .some((group) => {
            let heightOfLastUncollapsedGroup = 0;

            if (
              group.groupName.toLowerCase() === "color" ||
              group.groupName.toLowerCase() === "handle color" ||
              group.groupName.toLowerCase() === "frame color" ||
              group.groupName.toLowerCase() === "seat color" ||
              group.groupName.toLowerCase() === "back color"
            ) {
              // we display maximum of 5 color items on each row
              heightOfLastUncollapsedGroup +=
                Math.ceil(group.groupItems.length / 5) * colorItemHeight +
                colorTopAndBottomMargin;
            } else {
              heightOfLastUncollapsedGroup += topAndBottomMargin;
              // we only display the first 8 group items if the group has 10 or more items
              if (group.groupItems.length >= 10) {
                heightOfLastUncollapsedGroup +=
                  getGroupItemsHeight(group.groupItems.slice(0, 8)) +
                  showMoreButtonHeight;
              } else {
                heightOfLastUncollapsedGroup += getGroupItemsHeight(
                  group.groupItems,
                );
              }
            }

            modifiedSectionGroups = modifiedSectionGroups.map(
              (sectionGroup) => {
                if (sectionGroup === group) {
                  return { ...sectionGroup, collapsed: true };
                }

                return sectionGroup;
              },
            );

            diff -= heightOfLastUncollapsedGroup;
            return diff < 0;
          });

        setState(modifiedSectionGroups);

        return done;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {compatibleModels && compatibleModels.length > 0 && (
        <PartsFinder
          compatibleModels={compatibleModels}
          pageUrl={pageUrl}
          isFilterRemoved={isFilterRemoved}
        />
      )}
      {sectionStateAndComponents.map(
        ({ Component, sectionGroups, setState, showComponent, inputId }) => (
          <React.Fragment key={Component.name}>
            {showComponent && (
              <Component
                groups={sectionGroups}
                groupsWithMultipleSelections={groupsWithMultipleSelections}
                prefetch={prefetch}
                toggleCollapsed={(clickedSectionGroup) => {
                  setUserInteraction(true);
                  setState(
                    sectionGroups.map((sectionGroup) =>
                      sectionGroup === clickedSectionGroup
                        ? {
                            ...sectionGroup,
                            collapsed: !clickedSectionGroup.collapsed,
                          }
                        : sectionGroup,
                    ),
                  );
                }}
                pageUrl={pageUrl}
                {...(Component === SearchWithinFilter && inputId
                  ? {
                      inputId,
                    }
                  : {})}
              />
            )}
          </React.Fragment>
        ),
      )}
    </>
  );
};

export default FilterList;
